import { useRef, useState } from "react";
import { apiRequest } from "../../services/api";
export default function SisSupplierCreditRelease({
  match,
  base,
  accessToken,
  allowed,
  onReleased,
  onBusyChange,
  blocked = false,
}) {
  const [open, setOpen] = useState(false),
    [reason, setReason] = useState(""),
    [proposal, setProposal] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [saved, setSaved] = useState(null);
  const pending = useRef(false),
    key = useRef(null),
    released = saved || match.release;
  async function preview() {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError("");
    setProposal(null);
    setChecks({});
    key.current = null;
    try {
      setProposal(
        await apiRequest(
          `${base}/supplier-credit-notes/matches/${match.id}/release-preview`,
          {
            method: "POST",
            accessToken,
            body: {
              date: new Date(Date.now() + 19800000).toISOString().slice(0, 10),
              reason,
            },
          },
        ),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  }
  async function execute() {
    if (
      pending.current ||
      !allowed ||
      !proposal ||
      !checks.original ||
      !checks.restore ||
      !checks.books
    )
      return;
    pending.current = true;
    setBusy(true);
    onBusyChange?.(true);
    setError("");
    try {
      key.current ||= window.crypto.randomUUID();
      const r = await apiRequest(
        `${base}/supplier-credit-notes/matches/${match.id}/release`,
        {
          method: "POST",
          accessToken,
          body: {
            date: proposal.snapshot.date,
            reason: proposal.snapshot.reason,
            previewHash: proposal.previewHash,
            requestKey: key.current,
            originalMatchReviewed: true,
            availabilityRestoredConfirmed: true,
            noFinancialChangeConfirmed: true,
          },
        },
      );
      setSaved(r);
      await onReleased?.(r);
    } catch (e) {
      setError(e.message);
    } finally {
      pending.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  }
  if (released)
    return (
      <div
        role="status"
        className="mt-2 rounded border border-amber-300 bg-amber-50 p-2"
      >
        <strong>Released — historical match, not an active allocation</strong>
        <p>{released.snapshot.reason}</p>
        <p>
          {released.snapshot.date} · Released by {released.createdBy}. Entire
          match availability restored; no financial entry or refund.
        </p>
      </div>
    );
  if (!allowed)
    return (
      <p className="mt-2 text-xs text-gray-500">
        Releasing a match requires both posting and reversal permissions.
      </p>
    );
  return (
    <section className="mt-2 rounded border p-3">
      <button
        type="button"
        disabled={busy || blocked}
        className="rounded border px-3 py-2"
        onClick={() => setOpen((v) => !v)}
      >
        Release incorrect document match
      </button>
      {open && (
        <fieldset disabled={busy || blocked} className="mt-3 space-y-2">
          <legend className="font-semibold">
            Release the entire retained match
          </legend>
          <p>
            This releases all allocations in this match, not the supplier note
            or the financial return credit. Original evidence stays immutable.
            Other matches remain active.
          </p>
          <label className="block">
            Supplier credit match release reason
            <textarea
              maxLength={1000}
              className="mt-1 w-full rounded border p-2"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setProposal(null);
                setChecks({});
                key.current = null;
                setError("");
              }}
            />
          </label>
          <button
            type="button"
            disabled={busy || reason.trim().length < 20}
            onClick={preview}
            className="rounded border px-3 py-2"
          >
            Preview release of supplier match
          </button>
          {error && (
            <p role="alert" className="text-red-700">
              {error}
            </p>
          )}
          {proposal && (
            <div className="space-y-2 rounded border border-amber-300 p-3">
              <p>
                Restore ₹{proposal.snapshot.amount} of document{" "}
                {proposal.snapshot.number}
              </p>
              {proposal.snapshot.restoresAllocations.map((a) => (
                <p key={a.sisRemainingCorrectionId ? `REMAINING:${a.sisRemainingCorrectionId}` : a.sisAdjustmentId ? `ADJUSTMENT:${a.sisAdjustmentId}` : a.sisReturnId} className="break-all">
                  {a.sisRemainingCorrectionId ? 'Post-return dues reduction' : a.sisAdjustmentId ? 'Dues reduction' : 'Return'} {a.sisRemainingCorrectionId || a.sisAdjustmentId || a.sisReturnId}: restore ₹{a.amount}
                </p>
              ))}
              <p>{proposal.warning}</p>
              {[
                [
                  "original",
                  "I reviewed the original match and all its allocations.",
                ],
                [
                  "restore",
                  "The entire match is released; document and recorded-credit availability are restored.",
                ],
                [
                  "books",
                  "No supplier dues, tax, stock, payment or refund changes are authorised.",
                ],
              ].map(([k, label]) => (
                <label key={k} className="block">
                  <input
                    type="checkbox"
                    checked={!!checks[k]}
                    onChange={(e) =>
                      setChecks((c) => ({ ...c, [k]: e.target.checked }))
                    }
                  />
                  {label}
                </label>
              ))}
              <button
                type="button"
                disabled={
                  busy || !checks.original || !checks.restore || !checks.books
                }
                className="rounded bg-red-700 px-3 py-2 text-white disabled:opacity-40"
                onClick={execute}
              >
                Release match and restore availability
              </button>
            </div>
          )}
        </fieldset>
      )}
    </section>
  );
}
